<template>
    <div>
        <!-- Compact: input-group style -->
        <div v-if="size === 'compact'" class="flex rounded">
            <span class="inline-flex items-center justify-center px-2 bg-gray-50 rounded-l border border-gray-300 overflow-hidden">
                <template v-if="$slots.empty_placeholder && !theSrc">
                    <slot name="empty_placeholder"></slot>
                </template>
                <template v-else-if="$slots.uploaded_placeholder && theSrc">
                    <slot name="uploaded_placeholder"></slot>
                </template>
                <template v-else>
                    <img :src="theSrc" class="block w-5 h-5 object-contain"/>
                </template>
                <svg v-if="showSpinner" class="animate-spin h-4 w-4 absolute text-gray-500"
                     fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 24 24" width="24" height="24">
                    <path fill="none" d="M0 0h24v24H0z"/>
                    <path d="M3.055 13H5.07a7.002 7.002 0 0 0 13.858 0h2.016a9.001 9.001 0 0 1-17.89 0zm0-2a9.001 9.001 0 0 1 17.89 0H18.93a7.002 7.002 0 0 0-13.858 0H3.055z"/>
                </svg>
            </span>
            <div class="flex flex-grow items-center justify-between -ml-px px-3 py-2 bg-white rounded-r border border-gray-300">
                <template v-if="title.endsWith('.pdf')">
                    <a :href="theSrc" class="cursor-pointer text-sm truncate" target="_blank" rel="noopener noreferrer">{{ title }}</a>
                </template>
                <template v-else>
                    <span class="text-sm truncate text-gray-700">{{ title }}</span>
                </template>
                <div class="flex items-center space-x-2 ml-2">
                    <!-- Delete button: show if file is uploaded or external URL is set -->
                    <button v-if="hasContent" type="button" class="inline-flex justify-center items-center focus:outline-none cursor-pointer" title="Delete" v-on:click="deleteFile()">
                        <svg class="w-4 h-4 text-gray-400 hover:text-red-600" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"></path>
                            <path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"></path>
                        </svg>
                    </button>
                    <!-- Upload button (no content) or Re-upload button (file upload only, not external URL) -->
                    <label v-if="!hasContent || isFileUpload" class="inline-flex justify-center items-center focus:outline-none cursor-pointer relative group" :title="!hasContent ? 'Upload' : 'Re-upload'">
                        <input
                            class="absolute inset-0 opacity-0 cursor-pointer"
                            ref="file"
                            type="file"
                            :name="name"
                            :accept="accept"
                            v-on:change="uploadFile()"
                        >
                        <!-- Upload icon (no content) -->
                        <svg v-if="!hasContent" class="w-4 h-4 text-gray-400 group-hover:text-gray-900 pointer-events-none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z"/>
                        </svg>
                        <!-- Re-upload icon (file upload) -->
                        <svg v-else class="w-4 h-4 text-gray-400 group-hover:text-gray-900 pointer-events-none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M5.463 4.433A9.961 9.961 0 0 1 12 2c5.523 0 10 4.477 10 10 0 2.136-.67 4.116-1.81 5.74L17 12h3A8 8 0 0 0 6.46 6.228l-.997-1.795zm13.074 15.134A9.961 9.961 0 0 1 12 22C6.477 22 2 17.523 2 12c0-2.136.67-4.116 1.81-5.74L7 12H4a8 8 0 0 0 13.54 5.772l.997 1.795z"/>
                        </svg>
                    </label>
                    <template v-if="$slots.custom_actions">
                        <slot name="custom_actions"></slot>
                    </template>
                </div>
            </div>
        </div>
        <validation-errors v-if="size === 'compact' && show_validation_errors_within_uploader" :errors="errors" class="mt-1" />

        <!-- Dropzone: drag & drop style -->
        <div v-else-if="size === 'dropzone'">
            <!-- Hidden file input -->
            <input
                class="hidden"
                ref="file"
                type="file"
                :name="name"
                :accept="accept"
                v-on:change="uploadFile()"
            >
            <!-- Has content state - not a label to allow delete button to work -->
            <div
                v-if="hasContent && !showSpinner"
                class="w-full rounded p-4 border border-dashed border-teal-300 bg-teal-50 flex items-center justify-center"
            >
                <div class="flex items-center gap-3 text-teal-700 w-full">
                    <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                    <span class="text-sm truncate flex-1">{{ title }}</span>
                    <button
                        type="button"
                        class="text-gray-400 hover:text-red-600 flex-shrink-0"
                        title="Delete"
                        @click="deleteFile()"
                    >
                        <svg class="w-4 h-4" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"></path>
                            <path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"></path>
                        </svg>
                    </button>
                    <button
                        type="button"
                        class="text-gray-400 hover:text-teal-600 flex-shrink-0"
                        title="Replace"
                        @click="$refs.file.click()"
                    >
                        <svg class="w-4 h-4" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M5.463 4.433A9.961 9.961 0 0 1 12 2c5.523 0 10 4.477 10 10 0 2.136-.67 4.116-1.81 5.74L17 12h3A8 8 0 0 0 6.46 6.228l-.997-1.795zm13.074 15.134A9.961 9.961 0 0 1 12 22C6.477 22 2 17.523 2 12c0-2.136.67-4.116 1.81-5.74L7 12H4a8 8 0 0 0 13.54 5.772l.997 1.795z"/>
                        </svg>
                    </button>
                </div>
            </div>
            <!-- Loading or empty state - clickable area -->
            <div
                v-else
                class="w-full rounded cursor-pointer p-4 border border-dashed transition-colors flex items-center justify-center"
                :class="[
                    isDragging
                        ? 'border-gray-400 bg-gray-100'
                        : 'border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100'
                ]"
                @click="$refs.file.click()"
                @dragover.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @drop.prevent="handleDrop"
            >
                <!-- Loading state -->
                <div v-if="showSpinner" class="flex items-center gap-3 text-gray-500">
                    <svg class="animate-spin h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span class="text-sm">Uploading...</span>
                </div>
                <!-- Empty state -->
                <div v-else class="flex items-center gap-3 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                        <path d="M7 18a4.6 4.4 0 0 1 0 -9a5 4.5 0 0 1 11 2h1a3.5 3.5 0 0 1 0 7h-1"></path>
                        <polyline points="9 15 12 12 15 15"></polyline>
                        <line x1="12" y1="12" x2="12" y2="21"></line>
                    </svg>
                    <span class="text-sm">{{ title }}</span>
                </div>
            </div>
            <validation-errors v-if="show_validation_errors_within_uploader" :errors="errors" class="mt-1" />
        </div>

        <!-- Default: original style -->
        <div v-else class="rounded border bg-white">
            <div class="flex justify-between handle" :class="sizeClasses.container">
                <div class="flex items-center truncate" :class="sizeClasses.spacing">
                    <div
                        class="flex justify-center items-center rounded border overflow-hidden relative"
                        :class="[sizeClasses.preview, hasContent ? 'bg-gray-100 border-gray-900' : 'bg-gray-50']">

                        <template v-if="$slots.empty_placeholder && !theSrc">
                            <slot name="empty_placeholder"></slot>
                        </template>

                        <template v-if="$slots.uploaded_placeholder && theSrc">
                            <slot name="uploaded_placeholder"></slot>
                        </template>

                        <template v-if="!$slots.empty_placeholder">
                            <img :src="theSrc" class="block rounded h-auto" :class="sizeClasses.image"/>
                        </template>

                        <!-- Uploaded checkmark indicator -->
                        <div v-if="hasContent && !showSpinner" class="absolute top-0 right-0 bg-gray-900 rounded-bl p-0.5">
                            <svg class="w-2.5 h-2.5 text-white" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                <path fill="none" d="M0 0h24v24H0z"/>
                                <path d="M10 15.172l9.192-9.193 1.415 1.414L10 18l-6.364-6.364 1.414-1.414z"/>
                            </svg>
                        </div>

                        <svg v-if="showSpinner" class="animate-spin absolute text-gray-500"
                             :class="sizeClasses.spinner"
                             fill="currentColor" xmlns="http://www.w3.org/2000/svg"
                             viewBox="0 0 24 24" width="24" height="24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path
                                d="M3.055 13H5.07a7.002 7.002 0 0 0 13.858 0h2.016a9.001 9.001 0 0 1-17.89 0zm0-2a9.001 9.001 0 0 1 17.89 0H18.93a7.002 7.002 0 0 0-13.858 0H3.055z"/>
                        </svg>
                    </div>
                    <div class="flex flex-col min-w-0">
                        <template v-if="title.endsWith('.pdf')">
                            <a :href="theSrc" class="cursor-pointer text-sm truncate" target="_blank" rel="noopener noreferrer">{{ title }}</a>
                        </template>
                        <template v-else>
                            <span class="text-sm truncate">{{ title }}</span>
                        </template>
                        <span v-if="isFileUpload" class="text-xs text-gray-500">Uploaded</span>
                        <span v-else-if="isExternalUrl" class="text-xs text-gray-500">External link</span>
                    </div>
                </div>
                <div class="flex items-center space-x-2" :class="sizeClasses.actions">
                    <!-- Delete button: show if file is uploaded or external URL is set -->
                    <button v-if="hasContent" type="button" class="inline-flex justify-center items-center focus:outline-none cursor-pointer" title="Delete" v-on:click="deleteFile()">
                        <svg class="w-5 h-5 text-gray-500 hover:text-red-600" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"></path>
                            <path d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"></path>
                        </svg>
                    </button>
                    <!-- Upload button (no content) or Re-upload button (file upload only, not external URL) -->
                    <label v-if="!hasContent || isFileUpload" class="inline-flex justify-center items-center focus:outline-none cursor-pointer relative group" :title="!hasContent ? 'Upload' : 'Re-upload'">
                        <input
                            class="absolute inset-0 opacity-0 cursor-pointer"
                            ref="file"
                            type="file"
                            :name="name"
                            :accept="accept"
                            v-on:change="uploadFile()"
                        >
                        <!-- Upload icon (no content) -->
                        <svg v-if="!hasContent" class="w-5 h-5 text-gray-500 group-hover:text-gray-900 pointer-events-none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M4 19h16v-7h2v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-8h2v7zm9-10v7h-2V9H6l6-6 6 6h-5z"/>
                        </svg>
                        <!-- Re-upload icon (file upload) -->
                        <svg v-else class="w-5 h-5 text-gray-500 group-hover:text-gray-900 pointer-events-none" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                            <path fill="none" d="M0 0h24v24H0z"/>
                            <path d="M5.463 4.433A9.961 9.961 0 0 1 12 2c5.523 0 10 4.477 10 10 0 2.136-.67 4.116-1.81 5.74L17 12h3A8 8 0 0 0 6.46 6.228l-.997-1.795zm13.074 15.134A9.961 9.961 0 0 1 12 22C6.477 22 2 17.523 2 12c0-2.136.67-4.116 1.81-5.74L7 12H4a8 8 0 0 0 13.54 5.772l.997 1.795z"/>
                        </svg>
                    </label>
                    <template v-if="$slots.custom_actions">
                        <slot name="custom_actions"></slot>
                    </template>
                </div>
            </div>
            <validation-errors v-if="show_validation_errors_within_uploader" :errors="errors" />
        </div>
    </div>
</template>

<script>
import ValidationErrors from "../../components/common/ValidationErrors.vue";

export default {
    components: {ValidationErrors},
    emits: ['upload:done', 'delete:done', 'upload:error', 'update:src'],
    props: {
        src: {
            type: String,
            default: null
        },
        no_src: {
            type: String,
            default: null
        },
        path: {
            type: String,
            default: null
        },
        accept: {
            type: String,
            default: 'image/*'
        },
        name: {
            type: String,
            default: 'file'
        },
        title: {
            type: String,
            default: 'Upload file'
        },
        upload_url: {
            type: String,
            required: true
        },
        delete_url: {
            type: String,
            required: true
        },
        additional_data: {
            type: Object,
            default: () => ({})
        },
        dynamic_action_buttons: {
            type: Boolean,
            default: false
        },
        show_validation_errors_within_uploader: {
            type: Boolean,
            default: true
        },
        size: {
            type: String,
            default: 'default',
            validator: (value) => ['default', 'compact', 'dropzone'].includes(value)
        }
    },
    data() {
        return {
            file: {},
            showSpinner: false,
            deletePath: this.path ?? null,
            dynamicActionButtons: this.dynamic_action_buttons ?? false,
            showUploadButton: this.show_upload_button ?? true,
            internalSrc: this.src ?? this.no_src,
            errors: null,
            isDragging: false,
            imageError: false
        }
    },
    watch: {
        path(newVal, oldVal) {
            if (newVal === oldVal) {
                return;
            }
            this.deletePath = newVal;
        },
        src(newVal) {
            this.internalSrc = newVal ?? this.no_src;
            this.imageError = false; // Reset error state when src changes
        }
    },
    computed: {
        theSrc: {
            get() {
                return this.internalSrc ?? this.no_src;
            },
            set(value) {
                const hasListener = this.$attrs['onUpdate:src'] || this.$.vnode.props?.['onUpdate:src'];
                if (hasListener) {
                    this.$emit('update:src', value);
                } else {
                    this.internalSrc = value;
                }
            }
        },
        hasContent() {
            return this.deletePath || (this.internalSrc && this.internalSrc !== this.no_src);
        },
        isExternalUrl() {
            return !this.deletePath && this.internalSrc && this.internalSrc !== this.no_src;
        },
        isFileUpload() {
            return !!this.deletePath;
        },
        sizeClasses() {
            if (this.size === 'compact') {
                return {
                    container: 'py-1.5 px-2 pr-3',
                    spacing: 'space-x-2.5',
                    preview: 'w-7 h-7',
                    image: 'max-h-7',
                    spinner: 'h-4 w-4',
                    actions: 'pl-2 space-x-2'
                };
            }
            // default
            return {
                container: 'p-2 pr-4',
                spacing: 'space-x-4',
                preview: 'p-2 w-12 h-12',
                image: 'max-h-12',
                spinner: 'h-8 w-8',
                actions: 'pl-4 space-x-4'
            };
        }
    },
    methods: {
        deleteFile() {
            const self = this;

            // If no path (external URL only), just clear locally and emit
            if (!this.deletePath) {
                // Set internal display to placeholder, but emit null to parent
                this.internalSrc = this.no_src;
                this.$emit('update:src', null);
                this.$emit('delete:done', true);
                return;
            }

            this.showSpinner = true;

            axios
                .post(this.delete_url, {
                    name: this.name,
                    path: this.deletePath,
                    additional_data: JSON.stringify(this.additional_data ?? {})
                })
                .then(function () {
                    // Set internal display to placeholder, but emit null to parent
                    self.internalSrc = self.no_src;
                    self.$emit('update:src', null);
                    self.deletePath = null;
                    self.$emit('delete:done', true);
                })
                .catch(function (e) {
                    self.$emit('delete:done', false);
                })
                .finally(function () {
                    self.showSpinner = false;
                });
        },
        uploadFile() {

            if (this.$refs.file.files[0] === undefined) {
                return;
            }

            const self = this;
            this.showSpinner = true;
            const formData = new FormData();

            formData.append('name', this.name);
            formData.append(self.name, this.$refs.file.files[0]);
            formData.append('additional_data', JSON.stringify(this.additional_data ?? {}));

            axios
                .post(this.upload_url, formData, {headers: {'Content-Type': 'multipart/form-data'}})
                .then(function (response) {
                    const url = response?.data?.uploads?.[self.name]?.url ?? null;
                    const path = response?.data?.uploads?.[self.name]?.path ?? null;

                    if (url !== null) {
                        self.theSrc = url;
                    }

                    if (path !== null) {
                        self.deletePath = path;
                    }

                        self.errors = null;
                        self.imageError = false; // Reset error state on successful upload
                        self.$emit('upload:done', response?.data?.uploads || {});
                })
                .catch(function (error) {
                    if(error.response?.status === 422) {
                        self.errors = error?.response?.data?.errors || {error: 'File validation upload error'};
                    } else {
                        self.errors = { error: 'File upload error' };
                    }
                    self.$emit('upload:error', error.response?.data?.errors || { error: 'File upload error' });
                })
                .finally(function () {
                    self.showSpinner = false;
                    if (self.$refs.file && self.$refs.file.files && self.$refs.file.files[0]) {
                        self.$refs.file.files[0].value = null;
                    }
                });
        },
        handleDrop(event) {
            this.isDragging = false;
            const files = event.dataTransfer?.files;
            if (files && files.length > 0) {
                const file = files[0];
                
                // Validate file type matches accept attribute
                if (this.accept && !this.isFileTypeAccepted(file)) {
                    this.errors = { file: ['File type not accepted. Please upload a valid file.'] };
                    return;
                }
                
                // Create a DataTransfer to set files on the input
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(file);
                this.$refs.file.files = dataTransfer.files;
                this.uploadFile();
            }
        },
        isFileTypeAccepted(file) {
            if (!this.accept) return true;
            
            const acceptedTypes = this.accept.split(',').map(t => t.trim());
            const fileType = file.type;
            const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
            
            return acceptedTypes.some(accepted => {
                // Handle wildcards like "image/*"
                if (accepted.endsWith('/*')) {
                    const category = accepted.slice(0, -2);
                    return fileType.startsWith(category + '/');
                }
                // Handle MIME types
                if (accepted.includes('/')) {
                    return fileType === accepted;
                }
                // Handle extensions like ".jpg"
                return fileExtension === accepted.toLowerCase();
            });
        }
    }
}
</script>
